import { IStemdoer, Stemdoer } from '@/shared/model/stemdoer.model';
import SAvatarStemdoer from '@/components/stemdo-components/s-avatar-stemdoer/SAvatarStemdoer.vue';
import SButton from '@/components/stemdo-components/s-button/SButton.vue';
import { defineComponent, onMounted, ref, watch, computed, inject } from 'vue';
import { useStore } from '@/plugins/vuex';
import { IRate } from '@/shared/model/rate.model';
import { useI18N } from '@/plugins/i18n';
import { ExperienceCollection } from '@/shared/model/experience-collection.model'; //need it when come from teamproposal
import SReadMore from '@/components/stemdo-components/s-read-more/SReadMore.vue';
import SLanguageIcon from '@/components/stemdo-components/s-language-icon/SLanguageIcon.vue';
import { StemdoerStatus } from '@/shared/model/enumerations/stemdoer-status.model';
import StemdoerAvailability from './StemdoerAvailability.vue';
import SShareLink from '@/components/stemdo-components/s-share-link/SShareLink.vue';
import SPopUp from '@/components/stemdo-components/s-pop-up/SPopUp.vue';
import SBoxStemdoerCompsAndCerts from '@/components/stemdo-components/s-box-stemdoer-comps-and-certs/SBoxStemdoerCompsAndCerts.vue';
import SBoxStemdoerExperience from '@/components/stemdo-components/s-box-stemdoer-experience/SBoxStemdoerExperience.vue';
import SBoxStemdoerEducation from '@/components/stemdo-components/s-box-stemdoer-education/SBoxStemdoerEducation.vue';
import CompanyService from '@/sections/admin/entities/company/company.service';

export default defineComponent({
  props: {
    stemdoer: {
      type: Object as () => IStemdoer,
      required: true
    },
    rates: {
      type: Array as () => IRate[],
      required: false
    },
    rate: {
      type: Number,
      required: false
    },
    onAddToCart: {
      type: Function,
      required: true
    },
    company: {
      type: Object as () => any,
      required: false
    }
  },
  components: {
    SAvatarStemdoer,
    SReadMore,
    SButton,
    SLanguageIcon,
    StemdoerAvailability,
    SPopUp,
    SBoxStemdoerCompsAndCerts,
    SBoxStemdoerExperience,
    SBoxStemdoerEducation,
    SShareLink
  },

  setup(props) {
    const store = useStore();
    const i18n = useI18N();
    const companyService = inject('companyService') as CompanyService;
    const stemdoerMutable = ref<Stemdoer>(new Stemdoer(props.stemdoer));
    const descriptionArray = ref([]);
    const loading = ref(false);
    const isManager = computed(() => store.getters.account?.authorities.includes('ROLE_GESTOR') ?? false);
    const cart = computed(() => store.getters['cartStore/cartItems']);
    const clientSelected = computed(() => store.getters['cartStore/clientSelected']);
    const companySelected = computed(() => store.getters['cartStore/companySelected']);
    const needHide = ref(true);
    const stemdoerisInCart = computed(() => {
      return cart.value.some(item => item.id === props.stemdoer['id']);
    });
    const sPopUpData = ref({
      open: false,
      text: ''
    });

    const originalRate = ref(props.rate);

    const getRate = async (seniority?: string) => {
      let ratesSource: any[] = [];
      if (isManager && props.company && Array.isArray(props.company.rates) && props.company.rates.length > 0) {
        ratesSource = props.company.rates;
      } else if (props.rates && props.rates.length > 0) {
        ratesSource = props.rates;
      } else if (companySelected.value && Array.isArray(companySelected.value.rates) && companySelected.value.rates.length > 0) {
        ratesSource = companySelected.value.rates;
      } else if (companySelected.value) {
        try {
          const response = await companyService.retrieveCompanyRates();
          if (Array.isArray(response) && response.length > 0) {
            ratesSource = response;
          }
        } catch (e) {
          console.error('Error retrieving company rates:', e);
        }
      }
      if (Array.isArray(ratesSource) && ratesSource.length > 0 && seniority) {
        const foundRate = ratesSource.find(
          item => item.seniority?.toLowerCase().replace(/[-_]/g, '') === seniority?.toLowerCase().replace(/[-_]/g, '')
        );
        if (foundRate && typeof foundRate.value === 'number') {
          return foundRate.value;
        }
      }
      return 36;
    };

    const currentRate = ref(36);
    const setCurrentRate = async () => {
      currentRate.value = await getRate(stemdoerMutable.value.seniority);
    };

    onMounted(() => {
      const urlParams = new URLSearchParams(window.location.search);
      needHide.value = !urlParams.has('h');
      stemdoerMutable.value.technologyScores.orderByScoreDesc();
      setCurrentRate();
    });

    watch(
      () => props.stemdoer,
      value => {
        sPopUpData.value.open = false;
        stemdoerMutable.value = new Stemdoer(value);
        stemdoerMutable.value.technologyScores.orderByScoreDesc();
        setCurrentRate();
      }
    );

    watch(
      [companySelected, () => stemdoerMutable.value.seniority, () => props.rates, () => props.rate],
      () => {
        setCurrentRate();
      },
      { deep: true }
    );

    const handleAddToCartBtn = () => {
      const stemdoerWithAvailability = props.stemdoer as { availability: { available: boolean } };
      if (stemdoerWithAvailability.availability.available) {
        props.onAddToCart((props.stemdoer as IStemdoer).id);
      }
    };

    const openPopUp = (open: boolean, text: string) => {
      sPopUpData.value.open = open;
      sPopUpData.value.text = text;
    };

    const getLanguageBadges = languages => {
      return languages.map(language => {
        const translationKey = 'explore.stemdoer-detail.language.' + language.name.toLowerCase();
        const translatedName = i18n.t(translationKey) as string;
        return {
          name: translatedName !== translationKey ? translatedName : language.name,
          level: language.level
        };
      });
    };

    return {
      stemdoerMutable,
      descriptionArray,
      loading,
      stemdoerisInCart,
      needHide,
      StemdoerStatus,
      sPopUpData,
      currentRate,
      originalRate,
      isManager,
      openPopUp,
      getRate,
      handleAddToCartBtn,
      getLanguageBadges
    };
  }
});
